/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { identityProviderConnectionsRecordAgent } from "../funcs/identityProviderConnectionsRecordAgent.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { OktaIdentityProviderConnection } from "../models/components/oktaidentityproviderconnection.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  RecordIdentityProviderConnectionAgentRequest,
  RecordIdentityProviderConnectionAgentSecurity,
} from "../models/operations/recordidentityproviderconnectionagent.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type RecordIdentityProviderConnectionAgentMutationVariables = {
  request: RecordIdentityProviderConnectionAgentRequest;
  security?: RecordIdentityProviderConnectionAgentSecurity | undefined;
  options?: RequestOptions;
};

export type RecordIdentityProviderConnectionAgentMutationData =
  OktaIdentityProviderConnection;

export type RecordIdentityProviderConnectionAgentMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * recordAgent identityProviderConnections
 *
 * @remarks
 * Record the Okta AI agent ID and the application it is bound to, for display. Okta does not expose these through its API. Requires org:admin.
 */
export function useRecordIdentityProviderConnectionAgentMutation(
  options?: MutationHookOptions<
    RecordIdentityProviderConnectionAgentMutationData,
    RecordIdentityProviderConnectionAgentMutationError,
    RecordIdentityProviderConnectionAgentMutationVariables
  >,
): UseMutationResult<
  RecordIdentityProviderConnectionAgentMutationData,
  RecordIdentityProviderConnectionAgentMutationError,
  RecordIdentityProviderConnectionAgentMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildRecordIdentityProviderConnectionAgentMutation(client, options),
    ...options,
  });
}

export function mutationKeyRecordIdentityProviderConnectionAgent(): MutationKey {
  return ["@gram/client", "identityProviderConnections", "recordAgent"];
}

export function buildRecordIdentityProviderConnectionAgentMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: RecordIdentityProviderConnectionAgentMutationVariables,
  ) => Promise<RecordIdentityProviderConnectionAgentMutationData>;
} {
  return {
    mutationKey: mutationKeyRecordIdentityProviderConnectionAgent(),
    mutationFn: function recordIdentityProviderConnectionAgentMutationFn({
      request,
      security,
      options,
    }): Promise<RecordIdentityProviderConnectionAgentMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(identityProviderConnectionsRecordAgent(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
